import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';

import {
  CompositeFilterDescriptor,
  process,
  State,
  DataResult
} from '@progress/kendo-data-query';

import {
  DataStateChangeEvent,
  GridComponent,
  CellClickEvent,
  FilterService
} from "@progress/kendo-angular-grid";

import {
  TasksListParameters,
  PS_Lst_Tasks_V2_Result,
  ScreenListsService,
  SharedDataService,
  LoggedUserService,
  Family,
  ContactStoredData,
  SimpleReponseResult,
  Profil,
  SupplierStatut,
} from 'ngx-paf-common';


import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { WindowService } from '@progress/kendo-angular-dialog';
import { DomSanitizer } from '@angular/platform-browser';


@Component({
  selector: 'lst-tasks',
  templateUrl: './lst-tasks.component.html',
  styleUrls: ['./lst-tasks.component.scss']
})
export class LstTasksComponent implements OnInit, OnDestroy {
  @ViewChild('grid') kendoGrid: GridComponent;
  public filters: TasksListParameters = new TasksListParameters();

  public lstStatuts: SupplierStatut[] = [];
  public lstFamilies: Family[] = [];
  public get lstProfils(): Profil[] {
    return this.sharedDataSvc.lstProfils;
  }

  userDatas: ContactStoredData[] = [];
  visibleFilters: boolean = true;
  tasks: PS_Lst_Tasks_V2_Result[];
  gridData: DataResult;
  state: State = {
    skip: 0,
    take: 100
  };
  public gridPageableSettings: any = {
    pageSizes: [10, 20, 50, 100],
    position: 'top'
  }


  constructor(
    public windowService: WindowService,
    private screenListsSvc: ScreenListsService,
    private router: Router,
    public sharedDataSvc: SharedDataService,
    private loggedUserSvc: LoggedUserService,
    private sanitizer: DomSanitizer) {
  }

  subscriptions: Subscription[] = [];
  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => {
      if (sub != null && !sub.closed) sub.unsubscribe();
    });
  }


  ngOnInit(): void {
    //on charge les listes fixes
    if (this.sharedDataSvc.allListsLoaded) {
      this.loadFixedLists();
    }    
    this.subscriptions.push(
      this.sharedDataSvc.allListsLoadedChange.subscribe(res => {
        this.loadFixedLists();
      }));
    //on charge les listes variables et les données
    //et on attend que le profil soit chargé
    if (this.sharedDataSvc.profilLoaded) {
      this.loadIfProfilAllowed();
    }
    this.subscriptions.push(
      this.sharedDataSvc.loggedProfilChange.subscribe(res => {
        this.loadIfProfilAllowed();
      })
    );
  }

  checkScreenAccess(): Promise<void> {
    let promise: Promise<void> = new Promise<void>((resolve, reject) => {
      this.sharedDataSvc.checkProfilAccessPafSettings('MY_TASKS') ? resolve() : reject('Votre profil ne vous autorise pas à accéder à cet écran');
    });
    promise.catch((error: string) => {
      this.sharedDataSvc.alert(error,
        (): void => {
          this.sharedDataSvc.redirectToDefaultScreen();
        });
    });
    return promise;
  }

  loadIfProfilAllowed(): void {
    this.checkScreenAccess()
      .then(() => {
        this.reloadVariableLists((): void => {
          this.search();
        });
      });
  }


  loadFixedLists(): void {
    this.lstFamilies = [...this.sharedDataSvc.families];
    this.lstStatuts = [...this.sharedDataSvc.supplierStatuts];
    this.lstFamilies.forEach((f: Family) => {
      (<any>f).Display = this.sharedDataSvc.familyLabel(f.Id);
    });
    this.lstFamilies.sortObject('Display');
    this.lstStatuts.forEach((s: SupplierStatut, index: number) => {
      (<any>s).traduction = this.sharedDataSvc.getLoadedLabel(s.LblCod);
    });
    this.lstStatuts.sortObject('traduction');
  }


  reloadVariableLists(callBack: () => any = null): void {
    this.userDatas = [];
    forkJoin([
      this.loggedUserSvc.LoggedUserStoredDatas(),
    ]).subscribe(results => {
      this.userDatas = results[0];
      this.loadFilters();
      if (callBack) callBack();
    }
    );
  }

  storeFiltersOnchange: boolean = false;
  loadFilters(): void {
    if (this.userDatas.some(ud => ud.Key == 'TASKS-LIST-FILTERS')) {
      this.filters = JSON.parse(this.userDatas.find(ud => ud.Key == 'TASKS-LIST-FILTERS').Datas);
    } else {
      this.reinit();
    }
    if (this.userDatas.some(ud => ud.Key == 'TASKS-LIST-GRIDSTATE')) {
      this.state = JSON.parse(this.userDatas.find(ud => ud.Key == 'TASKS-LIST-GRIDSTATE').Datas);
    }
    this.storeFiltersOnchange = true;
  }

  storeFilters(): Observable<[SimpleReponseResult, SimpleReponseResult]> {
    if (!this.storeFiltersOnchange) {
      let fakeResponse: SimpleReponseResult = new SimpleReponseResult();
      fakeResponse.Ok = true;
      fakeResponse.Message = '';
      fakeResponse.Id = 0;
      return of([fakeResponse, fakeResponse]);
    } else {
      return forkJoin([
        this.loggedUserSvc.StoreDatas('TASKS-LIST-FILTERS', this.filters),
        this.loggedUserSvc.StoreDatas('TASKS-LIST-GRIDSTATE', this.state)
      ]);
    }
  }

  toggleDisplayFilters(): void {
    this.visibleFilters = !this.visibleFilters;
  }

  public reinit() {
    this.tasks = [];
    this.state = {
      skip: 0,
      take: 100
    };
    this.gridData = <DataResult>process(this.tasks, this.state);
    this.filters = new TasksListParameters();
    this.filters.TopMesTaches = true;
    this.filters.TopMemeNiveau = false;
    this.filters.TopAttente = false;
    this.search();
  }


  get TopMesTaches(): boolean {
    return this.filters.TopMesTaches;
  }
  set TopMesTaches(value: boolean) {
    if (this.filters.TopMesTaches != value) {
      this.filters.TopMesTaches = value;
      this.storeFilters().subscribe(res => this.refreshGrid());
    }
  }
  get TopMemeNiveau(): boolean {
    return this.filters.TopMemeNiveau;
  }
  set TopMemeNiveau(value: boolean) {
    if (this.filters.TopMemeNiveau != value) {
      this.filters.TopMemeNiveau = value;
      this.storeFilters().subscribe(res => this.refreshGrid());
    }
  }
  get TopAttente(): boolean {
    return this.filters.TopAttente;
  }
  set TopAttente(value: boolean) {
    if (this.filters.TopAttente != value) {
      this.filters.TopAttente = value;
      this.storeFilters().subscribe(res => this.refreshGrid());
    }
  }

  refreshGrid(): void {
    setTimeout(() => {
      this.gridData = process(this.filteredTasks, this.state);
    }, 0);
  }

  get filteredTasks(): PS_Lst_Tasks_V2_Result[] {
    return this.tasks.filter(item => {
      return (this.filters.TopMesTaches && item.TopMesTaches == 1)
        || (this.filters.TopMemeNiveau && item.TopMemeNiveau == 1)
        || (this.filters.TopAttente && item.TopAttente == 1)
    })
  }

  public search(): void {
    this.selectedRow = null;
    this.storeFilters()
      .subscribe((res: any) => {
        this.sharedDataSvc.WindowLock();
        this.filters.LngCod = this.sharedDataSvc.lngCod;
        this.screenListsSvc.TasksList(this.filters)
          .subscribe((res: PS_Lst_Tasks_V2_Result[]) => {
            res.forEach(item => {
              (<any>item).IsNucleaire = (item.TopNucleaire == 'O');
              (<any>item).familiesCellDisplay = this.sanitizer.bypassSecurityTrustHtml(this.getFamiliesCellDisplay(item));
            });
            this.tasks = res;
            if ((this.state.skip || 0) > res.length) {
              this.state = {
                skip: 0,
                take: 10
              };
            }
            this.gridData = process(this.filteredTasks, this.state);
            setTimeout((): void => { this.kendoGrid.autoFitColumns() }, 100);
          },
            (err): void => { this.sharedDataSvc.alert(JSON.stringify(err), (): void => this.sharedDataSvc.WindowUnlock()) },
            (): void => this.sharedDataSvc.WindowUnlock()
          );
      });
  }


  public familyClass(dataItem: Family, indented: boolean = false): any {
    let ngClass: any = this.sharedDataSvc.familyClass(dataItem.Id);
    if (indented) {
      let familyLevel: number = this.sharedDataSvc.familyLevel(dataItem.Id) + 1;
      ngClass['ml-' + familyLevel.toString()] = true;
    }
    return ngClass;
  }



  selectedRow: PS_Lst_Tasks_V2_Result = null;

  public selectRow(event: CellClickEvent): void {
    this.selectedRow = event.dataItem;
  }

  dblClickGridEvent(): void {
    if (!this.selectedRow) return;
    const url = this.router.createUrlTree([`supplier-details/${this.selectedRow.Id}/list-tasks`]);
    window.open(url.toString(), '_blank');
  }

  getSupplierLink(dataItem: PS_Lst_Tasks_V2_Result): string {
    return this.router.createUrlTree([`supplier-details/${dataItem.Id}/list-tasks`]).toString();
  }

  getFamiliesCellDisplay(item: PS_Lst_Tasks_V2_Result): string {
    let families: Family[] = this.lstFamilies
      .filter(f => (item.FamiliesIds || '').split(';').indexOf(f.Id.toString()) >= 0);
    if (families.length == 0) return '';

    let ret: string[] = ['<div class="d-flex flex-column" style="width:100;">'];
    const urlSupplierKnowledge: string = `${window.location.href}/../supplier-details/${item.Id}/list-tasks-knowledge`; //this.getSupplierLink(item)+'-knowledge';
    families.forEach(f => {
      const isMainFamily: boolean = f.Id == item.MainFamilyId;
      const divToadd: string = '<a target="_blank" style="width:100%;" href="' + urlSupplierKnowledge + '">' + (isMainFamily ? '<strong>' : '') + (<any>f).Display.replace('\'', '\\\'') + (isMainFamily ? '</strong>' : '') + '</a>';
      //on va mettre le coeur de métier en 1er pour qu'il soit toujours visible au chargement
      if (isMainFamily) {
        ret.splice(0, 0, divToadd);
      } else {
        ret.push(divToadd);
      }
    });
    ret.push('</div>');
    return ret.join('');
  }
  //---------------------------------------

  // getAchFamCellDisplay(item:PS_Lst_Tasks_V2_Result) : string {
  //   let ret: string[] = ['<div class="d-flex flex-column align-items-center" style="width:100;">'];
  //   (item.AchFamVal||'').split('|') .forEach(achfam=> {
  //     ret.push('<div class="badge cell-'+ item.AchFamColor +'">' + achfam + '</div>');
  //   })
  //   ret.push('</div>');
  //   return ret.join('');
  // }

  checkNgClass(fieldValue: string): any {
    return {
      'far': true,
      'fa-check-square': (fieldValue == 'O'),
      'fa-square': (fieldValue != 'O'),
    }
  }

  public gridFiltersChange(values: any[], filterService: FilterService, column: any): void {
    filterService.filter({
      filters: values.map((value) => ({
        field: column.field,
        operator: "eq",
        value,
      })),
      logic: "or",
    });
    this.kendoGrid.autoFitColumns();
  }

  public dataStateChange(state: DataStateChangeEvent): void {
    this.state = state;
    this.gridData = process(this.filteredTasks, this.state);
    this.kendoGrid.autoFitColumns();
  }



  //-------------------------------------
  // Filtres sur Grid

  public filterChange(filter: CompositeFilterDescriptor): void {
    this.state.filter = filter;
    let filterDatas: PS_Lst_Tasks_V2_Result[] = this.filteredTasks;
    this.gridData = process(filterDatas, this.state);
  }

  cellClass(itemColor: string): string {
    return itemColor ? 'cell-' + itemColor : '';
  }

}
